import * as THREE from 'three';
import {
  applySurfaceMaterialDefaults,
  copySurfaceMaterialProperties,
  createSurfaceMaterial,
  surfaceProgramCacheKey,
  type SurfaceMaterialParams,
} from '../core/SurfaceMaterial';
import { applyBakeShaderHooks } from '../core/SurfaceBakeChunks';
import {
  assignDamageFieldUniforms,
  DAMAGE_FIELD_COMMON_GLSL,
  DAMAGE_FIELD_GLSL,
  DAMAGE_FIELD_VERTEX_GLSL,
  DAMAGE_FIELD_VERTEX_SAMPLE,
  damageFieldProgramParts,
  registerDamageFieldConsumer,
} from '../core/DamageFieldBinding';
import {
  applyDamageDecalAtlasDefine,
  assignDamageDecalUniforms,
  DAMAGE_DECAL_ATLAS_DEFINE,
  DAMAGE_DECAL_ATLAS_GLSL,
  DAMAGE_DECAL_VERTEX_GLSL,
  DAMAGE_DECAL_VERTEX_SAMPLE,
} from '../core/DamageDecalAtlas';
import { SURFACE_MICRO_GLSL } from '../core/SurfaceMicroDetail';
import { SURFACE_NOISE_GLSL } from '../core/SurfaceNoise';
import { SURFACE_TRIPLANAR_GLSL } from '../core/TriplanarProjection';
import type { MaterialAppearanceDescriptor } from '../appearanceDescriptor';
import {
  asFiniteNumber,
  asHexColor,
  commonSurfaceControls,
  type SurfaceControlSpec,
  type SurfaceDefinition,
  type SurfaceFace,
} from '../core/SurfaceDefinition';
import { DESTRUCTION_PROFILES } from '../../services/destructionProfiles';

export type ConcreteQuality = 'high' | 'medium' | 'low';
export type ConcreteFace = 'exterior' | 'interior';
export type ConcreteSourceMode = 'procedural' | 'scanned-pbr';
/** Explicit surface weathering / finish state — independent of procedural vs scanned source. */
export type ConcreteSurfaceState = 'hero' | 'aged' | 'wet' | 'dusty' | 'aggregate';
export type MaterialParams = SurfaceMaterialParams;

export interface ConcreteMaterialSettings {
  /**
   * Real-world tile size in meters for both procedural and scanned paths.
   * Scanned triplanar UVs = restPosition / textureScale.
   * Procedural noise uses the same inverse-scale tile basis so mm/cm features stay coherent.
   */
  textureScale: number;
  roughness: number;
  normalStrength: number;
  macroVariation: number;
  moisture: number;
  interiorAmount: number;
  quality: ConcreteQuality;
  seed: number;
  /** Visual source for the shared concrete system. Default: procedural noise. */
  source: ConcreteSourceMode;
  /** Spatial surface state driving albedo/roughness/normal coherently. Default: hero. */
  surfaceState: ConcreteSurfaceState;
  /** Multiplier on shader albedo. Default white — identity. */
  baseTint: string;
  roughnessMin: number;
  roughnessMax: number;
  /** Extra dirt/age mix. Default 0 — identity. */
  wearAmount: number;
}

export const DEFAULT_CONCRETE_SETTINGS: ConcreteMaterialSettings = {
  textureScale: 0.55,
  roughness: 0.76,
  normalStrength: 0.22,
  macroVariation: 0.28,
  moisture: 0.05,
  interiorAmount: 0.7,
  quality: 'medium',
  seed: 17,
  source: 'procedural',
  surfaceState: 'hero',
  baseTint: '#ffffff',
  roughnessMin: 0.04,
  roughnessMax: 1,
  wearAmount: 0,
};

/** Pale cement plume + aggregate chips, matched to the procedural albedo. */
export const appearanceDescriptor = {
  version: 1,
  dust: { color: '#d8d2c6', lingerScale: 1.08, settleSpeed: 0.28 },
  chips: { color: '#c4c8cc' },
  debrisTint: '#a8aeb2',
  particleStyle: { plume: true },
  fragment: { outer: '#9a958c', interior: '#b5aea2' },
} as const satisfies MaterialAppearanceDescriptor;

/** ambientCG scan pair used by the scanned-PBR lab mode (CC0). */
export const SCANNED_CONCRETE_ATTRIBUTION = {
  provider: 'ambientCG',
  license: 'CC0',
  resolution: '1K JPG',
  exteriorId: 'Concrete014',
  interiorId: 'Concrete025',
  exteriorUrl: 'https://ambientcg.com/view?id=Concrete014',
  interiorUrl: 'https://ambientcg.com/view?id=Concrete025',
  exteriorRole: 'Intact cast concrete (clean light smooth cast surface)',
  interiorRole: 'Fresh exposed-aggregate fracture interior',
} as const;

const SCANNED_BASE = '/materials/concrete/ambientcg';

const SCANNED_ASSET_PATHS = {
  exterior: {
    color: `${SCANNED_BASE}/Concrete014_1K-JPG_Color.jpg`,
    normal: `${SCANNED_BASE}/Concrete014_1K-JPG_NormalGL.jpg`,
    roughness: `${SCANNED_BASE}/Concrete014_1K-JPG_Roughness.jpg`,
    ao: `${SCANNED_BASE}/Concrete014_1K-JPG_AmbientOcclusion.jpg`,
    height: `${SCANNED_BASE}/Concrete014_1K-JPG_Displacement.jpg`,
  },
  interior: {
    color: `${SCANNED_BASE}/Concrete025_1K-JPG_Color.jpg`,
    normal: `${SCANNED_BASE}/Concrete025_1K-JPG_NormalGL.jpg`,
    roughness: `${SCANNED_BASE}/Concrete025_1K-JPG_Roughness.jpg`,
    ao: `${SCANNED_BASE}/Concrete025_1K-JPG_AmbientOcclusion.jpg`,
    height: `${SCANNED_BASE}/Concrete025_1K-JPG_Displacement.jpg`,
  },
} as const;

export interface ScannedConcreteTextures {
  color: THREE.Texture;
  normal: THREE.Texture;
  roughness: THREE.Texture;
  ao: THREE.Texture;
  /** Height/displacement map used for scanned relief (optional asset, placeholder-safe). */
  height: THREE.Texture;
}

interface ConcreteUniforms {
  uConcreteTextureScale: { value: number };
  uConcreteNormalStrength: { value: number };
  uConcreteMacroVariation: { value: number };
  uConcreteMoisture: { value: number };
  uConcreteInteriorAmount: { value: number };
  uConcreteInteriorMode: { value: number };
  uConcreteQuality: { value: number };
  uConcreteSeed: { value: number };
  uConcreteSourceMode: { value: number };
  /** 0=hero 1=aged 2=wet 3=dusty 4=aggregate */
  uConcreteSurfaceState: { value: number };
  uConcreteMap: { value: THREE.Texture };
  uConcreteNormalMap: { value: THREE.Texture };
  uConcreteRoughnessMap: { value: THREE.Texture };
  uConcreteAoMap: { value: THREE.Texture };
  uConcreteHeightMap: { value: THREE.Texture };
  /** Secondary aggregate layer (Concrete025) for exterior aggregate state. */
  uConcreteAggMap: { value: THREE.Texture };
  uConcreteAggNormalMap: { value: THREE.Texture };
  uConcreteAggRoughnessMap: { value: THREE.Texture };
  uConcreteAggHeightMap: { value: THREE.Texture };
  uConcreteBaseTint: { value: THREE.Color };
  uConcreteRoughnessMin: { value: number };
  uConcreteRoughnessMax: { value: number };
  uConcreteWearAmount: { value: number };
}

interface ConcreteMaterialState {
  face: ConcreteFace;
  settings: ConcreteMaterialSettings;
  uniforms: ConcreteUniforms;
}

const concreteStates = new WeakMap<THREE.Material, ConcreteMaterialState>();

/** Shared scan textures — one set per face, reused by every concrete material/clone. */
const scannedPacks: Partial<Record<ConcreteFace, ScannedConcreteTextures>> = {};
let scannedLoadPromise: Promise<void> | null = null;

const placeholderColor = makePlaceholderTexture(0xc8c4bc);
const placeholderNormal = makePlaceholderTexture(0x8080ff);
const placeholderRoughness = makePlaceholderTexture(0xd0d0d0);
const placeholderAo = makePlaceholderTexture(0xffffff);
const placeholderHeight = makePlaceholderTexture(0x808080);

function makePlaceholderTexture(hex: number): THREE.DataTexture {
  const r = (hex >> 16) & 0xff;
  const g = (hex >> 8) & 0xff;
  const b = hex & 0xff;
  const data = new Uint8Array([r, g, b, 255]);
  const tex = new THREE.DataTexture(data, 1, 1);
  tex.needsUpdate = true;
  tex.colorSpace = THREE.NoColorSpace;
  tex.wrapS = THREE.RepeatWrapping;
  tex.wrapT = THREE.RepeatWrapping;
  return tex;
}

function configureMap(texture: THREE.Texture, colorSpace: THREE.ColorSpace): THREE.Texture {
  texture.colorSpace = colorSpace;
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.RepeatWrapping;
  texture.anisotropy = 4;
  texture.generateMipmaps = true;
  texture.minFilter = THREE.LinearMipmapLinearFilter;
  texture.magFilter = THREE.LinearFilter;
  texture.needsUpdate = true;
  return texture;
}

function loadTexture(url: string, colorSpace: THREE.ColorSpace): Promise<THREE.Texture> {
  return new Promise((resolve, reject) => {
    const loader = new THREE.TextureLoader();
    loader.load(
      url,
      (texture) => resolve(configureMap(texture, colorSpace)),
      undefined,
      (err) => reject(err instanceof Error ? err : new Error(`Failed to load ${url}`)),
    );
  });
}

async function loadPack(face: ConcreteFace): Promise<ScannedConcreteTextures> {
  const paths = SCANNED_ASSET_PATHS[face];
  const results = await Promise.allSettled([
    loadTexture(paths.color, THREE.SRGBColorSpace),
    loadTexture(paths.normal, THREE.NoColorSpace),
    loadTexture(paths.roughness, THREE.NoColorSpace),
    loadTexture(paths.ao, THREE.NoColorSpace),
    loadTexture(paths.height, THREE.NoColorSpace),
  ]);
  const failure = results.find((result): result is PromiseRejectedResult => result.status === 'rejected');
  if (failure) {
    for (const result of results) {
      if (result.status === 'fulfilled') result.value.dispose();
    }
    throw failure.reason instanceof Error ? failure.reason : new Error(`Failed to load ${face} concrete maps`);
  }
  const [color, normal, roughness, ao, height] = results.map(
    (result) => (result as PromiseFulfilledResult<THREE.Texture>).value,
  );
  return { color, normal, roughness, ao, height };
}

function disposePack(pack: ScannedConcreteTextures): void {
  pack.color.dispose();
  pack.normal.dispose();
  pack.roughness.dispose();
  pack.ao.dispose();
  pack.height.dispose();
}

/** Ensure ambientCG scan maps are loaded once and shared. Safe to call repeatedly. */
export function ensureScannedConcreteTextures(): Promise<void> {
  if (scannedPacks.exterior && scannedPacks.interior) {
    return Promise.resolve();
  }
  if (!scannedLoadPromise) {
    scannedLoadPromise = Promise.allSettled([loadPack('exterior'), loadPack('interior')])
      .then((results) => {
        const failure = results.find((result): result is PromiseRejectedResult => result.status === 'rejected');
        if (failure) {
          for (const result of results) {
            if (result.status === 'fulfilled') disposePack(result.value);
          }
          throw failure.reason instanceof Error ? failure.reason : new Error('Failed to load concrete scan maps');
        }
        const [exterior, interior] = results.map(
          (result) => (result as PromiseFulfilledResult<ScannedConcreteTextures>).value,
        );
        scannedPacks.exterior = exterior;
        scannedPacks.interior = interior;
      })
      .catch((err) => {
        scannedLoadPromise = null;
        throw err;
      });
  }
  return scannedLoadPromise;
}

export function getScannedConcreteTextures(face: ConcreteFace): ScannedConcreteTextures | null {
  return scannedPacks[face] ?? null;
}

export function isScannedConcreteReady(): boolean {
  return Boolean(scannedPacks.exterior && scannedPacks.interior);
}

/** Compact numeric encoding for the surface-state uniform. */
export function surfaceStateUniformValue(state: ConcreteSurfaceState): number {
  switch (state) {
    case 'hero':
      return 0;
    case 'aged':
      return 1;
    case 'wet':
      return 2;
    case 'dusty':
      return 3;
    case 'aggregate':
      return 4;
    default:
      return 0;
  }
}

const CONCRETE_NOISE_GLSL = /* glsl */ `
uniform float uConcreteTextureScale;
uniform float uConcreteNormalStrength;
uniform float uConcreteMacroVariation;
uniform float uConcreteMoisture;
uniform float uConcreteInteriorAmount;
uniform float uConcreteInteriorMode;
uniform float uConcreteQuality;
uniform float uConcreteSeed;
uniform float uConcreteSourceMode;
uniform float uConcreteSurfaceState;
uniform sampler2D uConcreteMap;
uniform sampler2D uConcreteNormalMap;
uniform sampler2D uConcreteRoughnessMap;
uniform sampler2D uConcreteAoMap;
uniform sampler2D uConcreteHeightMap;
uniform sampler2D uConcreteAggMap;
uniform sampler2D uConcreteAggNormalMap;
uniform sampler2D uConcreteAggRoughnessMap;
uniform sampler2D uConcreteAggHeightMap;
uniform vec3 uConcreteBaseTint;
uniform float uConcreteRoughnessMin;
uniform float uConcreteRoughnessMax;
uniform float uConcreteWearAmount;

${SURFACE_TRIPLANAR_GLSL}
${SURFACE_NOISE_GLSL}

float concreteFbm(vec3 p, float wavelengthMeters, float fp) {
  float value = surfaceNoiseBL(p, uConcreteSeed * 19.19, wavelengthMeters, fp);
  float weight = 1.0;
  p = p * 2.03 + vec3(17.0, 43.0, 29.0);

  if (uConcreteQuality > 0.5) {
    value += surfaceNoiseBL(p, uConcreteSeed * 19.19, wavelengthMeters / 2.03, fp) * 0.5;
    weight += 0.5;
    p = p * 2.01 + vec3(31.0, 11.0, 47.0);
    value += surfaceNoiseBL(p, uConcreteSeed * 19.19, wavelengthMeters / 4.0803, fp) * 0.25;
    weight += 0.25;
  }

  if (uConcreteQuality > 1.5) {
    p = p * 2.07 + vec3(7.0, 53.0, 19.0);
    value += surfaceNoiseBL(p, uConcreteSeed * 19.19, wavelengthMeters / 8.4462, fp) * 0.125;
    weight += 0.125;
    p = p * 2.02 + vec3(61.0, 23.0, 37.0);
    value += surfaceNoiseBL(p, uConcreteSeed * 19.19, wavelengthMeters / 17.0613, fp) * 0.0625;
    weight += 0.0625;
  }

  return value / weight;
}

// Sparse cellular-like field: F1 distance to random feature points per cell.
float concreteCellular(vec3 p) {
  vec3 cell = floor(p);
  vec3 f = fract(p);
  float minDist = 1.0;
  for (int k = -1; k <= 1; k++) {
    for (int j = -1; j <= 1; j++) {
      for (int i = -1; i <= 1; i++) {
        vec3 g = vec3(float(i), float(j), float(k));
        vec3 o = vec3(
          surfaceHash_(cell + g, uConcreteSeed * 19.19),
          surfaceHash_(cell + g + vec3(19.0, 7.0, 3.0), uConcreteSeed * 19.19),
          surfaceHash_(cell + g + vec3(5.0, 23.0, 11.0), uConcreteSeed * 19.19)
        );
        vec3 r = g + o - f;
        minDist = min(minDist, dot(r, r));
      }
    }
  }
  return sqrt(minDist);
}

// Keep the concrete shader vocabulary stable while projection is shared with
// stone, brick, and future scanned surfaces.
vec3 concreteTriplanarWeights(vec3 n) { return surfaceTriplanarWeights(n); }
vec4 concreteSampleTriplanar(sampler2D tex, vec3 pos, vec3 weights) {
  return surfaceSampleTriplanar(tex, pos, weights);
}
vec3 concreteSampleTriplanarNormal(sampler2D nmap, vec3 pos, vec3 weights, vec3 restNormal) {
  return surfaceSampleTriplanarNormal(nmap, pos, weights, restNormal);
}

// Concrete-specific per-fragment fields at distinct physical scales.
// textureScale is meters/tile (same contract as scanned triplanar).
// Interior (break-face): quality low = cement + cheap pores; medium = +pores;
// high = full cellular aggregate. Exterior (formed face): sparse air pits only —
// dense pinhole/bug-hole/aggregate flecks belong on the fracture interior.
void concreteSurfaceFields(
  vec3 restPos,
  out float cement,
  out float pores,
  out float aggregate,
  out float height
) {
  float metersPerTile = max(uConcreteTextureScale, 0.05);
  // Match scanned semantics: rest meters → tile space via 1 / metersPerTile.
  vec3 p = restPos * (1.0 / metersPerTile);
  float fp = surfacePixelFootprint(restPos);

  // Cement grain — band-limit each octave to the fragment footprint so
  // high-frequency analytic noise cannot crawl under camera motion.
  cement = surfaceNoiseBL(p * 28.0 + vec3(3.0, 11.0, 7.0), uConcreteSeed * 19.19, metersPerTile / 28.0, fp);
  if (uConcreteQuality > 0.5) {
    cement = cement * 0.68 + surfaceNoiseBL(p * 48.0 + vec3(19.0, 5.0, 31.0), uConcreteSeed * 19.19, metersPerTile / 48.0, fp) * 0.32;
  }
  if (uConcreteQuality > 1.5) {
    cement = cement * 0.82 + surfaceNoiseBL(p * 76.0 + vec3(7.0, 41.0, 13.0), uConcreteSeed * 19.19, metersPerTile / 76.0, fp) * 0.18;
  }

  if (uConcreteInteriorMode < 0.5) {
    // Formed exterior: no baked pinholes/aggregate. Sparse air pits are applied
    // live (camera-footprint faded) so they cannot speckle the wide bake.
    pores = 0.0;
    aggregate = 0.0;
    height = 0.5 + (cement - 0.5) * 0.012;
  } else {
    // Sparse air voids / pinholes — fade + widen the threshold so pores vanish
    // instead of strobing when their wavelength drops below a pixel.
    float fade36 = surfaceOctaveFade(metersPerTile / 36.0, fp);
    float poreNoise = surfaceNoiseBL(p * 36.0 + vec3(17.0, 29.0, 4.0), uConcreteSeed * 19.19, metersPerTile / 36.0, fp);
    float bugHoles = 0.0;
    if (uConcreteQuality < 0.5) {
      pores = smoothstep(0.94 - 0.08 * (1.0 - fade36), 0.98, poreNoise) * 0.28 * fade36;
    } else {
      // Medium bug-holes: one restrained cellular scale plus a broad variation field.
      // This keeps a few centimetre-scale formwork voids readable on a wall without
      // turning the surface into uniformly noisy gravel. Keep this work out of low
      // quality, where the simpler pinhole field is the intended budget.
      float fadeBug = surfaceOctaveFade(metersPerTile / 12.0, fp);
      float bugHoleCell = concreteCellular(p * 12.0 + vec3(23.0, 5.0, 17.0));
      float bugHoleVariation = smoothstep(0.42, 0.78, surfaceNoiseBL(p * 5.5 + vec3(31.0, 13.0, 9.0), uConcreteSeed * 19.19, metersPerTile / 5.5, fp));
      bugHoles = (1.0 - smoothstep(0.08, 0.23, bugHoleCell)) * bugHoleVariation * fadeBug;
      pores = smoothstep(0.91 - 0.06 * (1.0 - fade36), 0.98, poreNoise) * 0.42 * fade36;
      pores = max(pores, bugHoles * 0.58);
      if (uConcreteQuality > 1.5) {
        float fade56 = surfaceOctaveFade(metersPerTile / 56.0, fp);
        float pores2 = smoothstep(
          0.94 - 0.08 * (1.0 - fade56),
          0.99,
          surfaceNoiseBL(p * 56.0 + vec3(41.0, 3.0, 22.0), uConcreteSeed * 19.19, metersPerTile / 56.0, fp)
        ) * fade56;
        pores = max(pores, pores2 * 0.28);
      }
    }

    // Aggregate / mineral flecks — small inclusions, not wall-scale pits.
    if (uConcreteQuality > 1.5) {
      float fadeCell = surfaceOctaveFade(metersPerTile / 24.0, fp);
      float cell = concreteCellular(p * 24.0 + vec3(2.0, 5.0, 1.0));
      aggregate = (1.0 - smoothstep(0.06, 0.22, cell)) * fadeCell;
      float mineral = surfaceNoiseBL(p * 18.0 + vec3(8.0, 2.0, 14.0), uConcreteSeed * 19.19, metersPerTile / 18.0, fp);
      aggregate *= smoothstep(0.52, 0.8, mineral);
    } else if (uConcreteQuality > 0.5) {
      float fadeAgg = surfaceOctaveFade(metersPerTile / 28.0, fp);
      float a = surfaceNoiseBL(p * 28.0 + vec3(2.0, 5.0, 1.0), uConcreteSeed * 19.19, metersPerTile / 28.0, fp);
      aggregate = smoothstep(0.76 - 0.08 * (1.0 - fadeAgg), 0.92, a) * fadeAgg;
    } else {
      float fadeAgg = surfaceOctaveFade(metersPerTile / 22.0, fp);
      float a = surfaceNoiseBL(p * 22.0 + vec3(2.0, 5.0, 1.0), uConcreteSeed * 19.19, metersPerTile / 22.0, fp);
      aggregate = smoothstep(0.86 - 0.08 * (1.0 - fadeAgg), 0.94, a) * 0.55 * fadeAgg;
    }

    // State semantics drive pore/aggregate amount (not textureScale alone).
    float poreAmt = 0.32;
    float aggAmt = 0.18;
    float st = uConcreteSurfaceState;
    if (st > 0.5 && st < 1.5) {
      // Aged: slightly more open voids, restrained mineral.
      poreAmt = 0.48;
      aggAmt = 0.24;
    } else if (st > 1.5 && st < 2.5) {
      // Wet: pores less readable under film; sparse flecks.
      poreAmt = 0.2;
      aggAmt = 0.14;
    } else if (st > 2.5 && st < 3.5) {
      // Dusty: coating hides micro voids / flecks.
      poreAmt = 0.12;
      aggAmt = 0.1;
    } else if (st > 3.5) {
      // Aggregate: clearly more mineral inclusions, still patchy not uniform.
      poreAmt = 0.28;
      aggAmt = 1.0;
    }
    pores *= poreAmt;
    aggregate *= aggAmt;

    // Scalar height: restrained — pinholes not gouges; flecks rise slightly.
    height = 0.5
      + (cement - 0.5) * 0.035
      - pores * 0.1
      + (aggregate - 0.15) * 0.07;
  }
}

// Stable rest-space state masks shared by procedural and scanned branches.
// Always fills macroField; specialized masks run only for the active surfaceState.
// Vertical runoff/streaks vary faster in X/Z and slowly along Y.
void concreteStateMasks(
  vec3 restPos,
  vec3 restN,
  out float macroField,
  out float agedMask,
  out float wetMask,
  out float dustMask,
  out float aggMask,
  out float runoffMask,
  out float efflorescenceMask,
  out float crackMask,
  out float edgeMoistureMask,
  out float poolMask
) {
  float metersPerTile = max(uConcreteTextureScale, 0.05);
  vec3 p = restPos * (1.0 / metersPerTile);
  float fp = surfacePixelFootprint(restPos);
  macroField = concreteFbm(p * 0.28 + vec3(13.0, 7.0, 29.0), metersPerTile / 0.28, fp);

  // Inactive masks stay zero so downstream state branches are free.
  agedMask = 0.0;
  wetMask = 0.0;
  dustMask = 0.0;
  aggMask = 0.0;
  runoffMask = 0.0;
  efflorescenceMask = 0.0;
  crackMask = 0.0;
  edgeMoistureMask = 0.0;
  poolMask = 0.0;

  float st = uConcreteSurfaceState;

  if (st > 0.5 && st < 1.5) {
    // Aged: broad mottling plus restrained vertical runoff (fast XZ, slow Y).
    float broad = concreteFbm(p * 0.2 + vec3(2.0, 9.0, 4.0), metersPerTile / 0.2, fp);
    float runoff = concreteFbm(vec3(p.x * 3.2, p.y * 0.32, p.z * 3.2) + vec3(5.0, 0.0, 11.0), metersPerTile / 3.2, fp);
    float narrow = concreteFbm(vec3(p.x * 6.0, p.y * 0.4, p.z * 6.0) + vec3(17.0, 3.0, 8.0), metersPerTile / 6.0, fp);
    float mottling = concreteFbm(p * 0.68 + vec3(31.0, 12.0, 6.0), metersPerTile / 0.68, fp);
    agedMask = clamp(
      smoothstep(0.36, 0.72, broad) * 0.32
        + smoothstep(0.48, 0.84, runoff) * 0.26
        + smoothstep(0.62, 0.9, narrow) * 0.16
        + smoothstep(0.32, 0.78, mottling) * 0.18,
      0.0,
      0.78
    );
    runoffMask = smoothstep(0.56, 0.88, runoff) * smoothstep(0.3, 0.82, narrow) * 0.62;
    // Mineral bloom is a separate pale deposit, kept sparse so scan color remains visible.
    float deposit = concreteFbm(vec3(p.x * 2.4, p.y * 0.24, p.z * 2.4) + vec3(41.0, 5.0, 19.0), metersPerTile / 2.4, fp);
    efflorescenceMask = smoothstep(0.68, 0.9, deposit) * (0.45 + 0.55 * (1.0 - abs(restN.y)));
    // Fine cracks/cavities are high-frequency and only appear within dirt coverage.
    float fadeCrack = surfaceOctaveFade(metersPerTile / 42.0, fp);
    float crackField = surfaceNoiseBL(vec3(p.x * 42.0, p.y * 1.3, p.z * 42.0) + vec3(8.0, 17.0, 3.0), uConcreteSeed * 19.19, metersPerTile / 42.0, fp);
    crackMask = smoothstep(0.84 - 0.08 * (1.0 - fadeCrack), 0.98, crackField) * agedMask * fadeCrack;
    edgeMoistureMask = smoothstep(0.3, 0.92, 1.0 - abs(restN.y)) * agedMask;
  } else if (st > 1.5 && st < 2.5) {
    // Wet: several mid-scale irregular patches + vertical streaks; dry islands remain.
    float wetPatchA = concreteFbm(p * 1.85 + vec3(21.0, 3.0, 8.0), metersPerTile / 1.85, fp);
    float wetPatchB = concreteFbm(p * 2.6 + vec3(7.0, 19.0, 13.0), metersPerTile / 2.6, fp);
    float wetStreak = concreteFbm(vec3(p.x * 2.2, p.y * 0.72, p.z * 1.1) + vec3(14.0, 6.0, 2.0), metersPerTile / 2.2, fp);
    float patches = max(
      smoothstep(0.54, 0.8, wetPatchA),
      smoothstep(0.58, 0.86, wetPatchB) * 0.85
    );
    float streaks = smoothstep(0.52, 0.82, wetStreak);
    float combined = max(patches * 0.76, streaks * 0.28);
    // Soft edge so film does not hard-cut, but keep coverage incomplete.
    wetMask = clamp(
      combined * mix(0.34, 0.82, clamp(uConcreteMoisture, 0.0, 1.0)),
      0.0,
      1.0
    );
    // Water pools at upward-facing contacts while vertical faces retain dry islands.
    poolMask = wetMask * clamp(restN.y * 0.65 + 0.35, 0.0, 1.0);
    edgeMoistureMask = wetMask * smoothstep(0.2, 0.9, 1.0 - abs(restN.y));
  } else if (st > 2.5 && st < 3.5) {
    // Dusty: broad pale coating on vertical walls (distinct from aged staining).
    float broad = concreteFbm(p * 0.16 + vec3(2.0, 9.0, 4.0), metersPerTile / 0.16, fp);
    float fine = concreteFbm(p * 0.85 + vec3(11.0, 4.0, 22.0), metersPerTile / 0.85, fp);
    float topness = clamp(restN.y * 0.35 + 0.6, 0.0, 1.0);
    float wallDust = 0.82 + 0.18 * (1.0 - abs(restN.y));
    dustMask = clamp(
      mix(0.55, 1.0, smoothstep(0.18, 0.62, broad * 0.7 + fine * 0.3))
        * wallDust
        * (0.85 + topness * 0.15),
      0.0,
      1.0
    );
  } else if (st > 3.5) {
    // Aggregate exposure patches — irregular, not whole-wall replace.
    float aggField = concreteFbm(p * 1.9 + vec3(4.0, 18.0, 9.0), metersPerTile / 1.9, fp);
    float cellish = surfaceNoiseBL(p * 3.4 + vec3(1.0, 6.0, 3.0), uConcreteSeed * 19.19, metersPerTile / 3.4, fp);
    // Note: "patch" is a GLSL reserved word — use aggPatchField.
    float aggPatchField = concreteFbm(p * 0.5 + vec3(9.0, 2.0, 15.0), metersPerTile / 0.5, fp);
    aggMask = clamp(
      smoothstep(0.32, 0.68, aggField * 0.42 + cellish * 0.38 + aggPatchField * 0.2)
        * mix(0.5, 1.0, clamp(uConcreteInteriorAmount, 0.0, 1.0)),
      0.0,
      1.0
    );
  }
}

// Derivative-based rest-space height normal. Flat height or zero strength → zero delta.
vec3 concreteHeightRestNormal(vec3 restN, vec3 restPos, float height, float strength) {
  return surfaceHeightRestNormal(restN, restPos, height, strength);
}
`;

const VERTEX_INJECTION = /* glsl */ `
attribute vec3 surfaceRestPosition;
varying vec3 vConcreteRestPosition;
varying vec3 vConcreteRestNormal;
${DAMAGE_FIELD_COMMON_GLSL}
${DAMAGE_FIELD_VERTEX_GLSL}
${DAMAGE_DECAL_VERTEX_GLSL}
// Vertex stage intentionally has no micro-normal FBM — only rest-space varyings.
`;

const FRAGMENT_INJECTION = /* glsl */ `
uniform mat3 normalMatrix;
varying vec3 vConcreteRestPosition;
varying vec3 vConcreteRestNormal;
${CONCRETE_NOISE_GLSL}
${SURFACE_MICRO_GLSL}
${DAMAGE_FIELD_GLSL}
${DAMAGE_DECAL_ATLAS_GLSL}
// Filled once in color_fragment; roughness/normal paths reuse without re-sampling noise.
float gConcreteCement;
float gConcretePores;
float gConcreteAggregate;
float gConcreteHeight;
float gConcreteMacroField;
float gConcreteAgedMask;
float gConcreteDirtMask;
float gConcreteWetMask;
float gConcreteDustMask;
float gConcreteAggMask;
float gConcreteRunoffMask;
float gConcreteEfflorescenceMask;
float gConcreteCrackMask;
float gConcreteEdgeMoistureMask;
float gConcretePoolMask;
float gConcreteDampMask;
float gConcreteMicroField;
float gConcreteLodFade;
`;

function clamp01(value: number): number {
  return Math.min(Math.max(Number.isFinite(value) ? value : 0, 0), 1);
}

function clampNormal(value: number): number {
  return Math.min(Math.max(Number.isFinite(value) ? value : 0, 0), 0.8);
}

function positive(value: number, fallback: number): number {
  return Number.isFinite(value) && value > 0 ? value : fallback;
}

function qualityValue(quality: ConcreteQuality): number {
  switch (quality) {
    case 'high':
      return 2;
    case 'medium':
      return 1;
    case 'low':
      return 0;
  }
}

function normalizeSource(source: ConcreteSourceMode | undefined): ConcreteSourceMode {
  return source === 'scanned-pbr' ? 'scanned-pbr' : 'procedural';
}

function normalizeSurfaceState(state: ConcreteSurfaceState | undefined): ConcreteSurfaceState {
  switch (state) {
    case 'aged':
    case 'wet':
    case 'dusty':
    case 'aggregate':
    case 'hero':
      return state;
    default:
      return 'hero';
  }
}

function normalizeSettings(settings: ConcreteMaterialSettings): ConcreteMaterialSettings {
  return {
    textureScale: positive(settings.textureScale, DEFAULT_CONCRETE_SETTINGS.textureScale),
    roughness: clamp01(settings.roughness),
    normalStrength: clampNormal(settings.normalStrength),
    macroVariation: clamp01(settings.macroVariation),
    moisture: clamp01(settings.moisture),
    interiorAmount: clamp01(settings.interiorAmount),
    quality: settings.quality,
    seed: Number.isFinite(settings.seed) ? settings.seed : DEFAULT_CONCRETE_SETTINGS.seed,
    source: normalizeSource(settings.source),
    surfaceState: normalizeSurfaceState(settings.surfaceState),
    baseTint: asHexColor(settings.baseTint, DEFAULT_CONCRETE_SETTINGS.baseTint),
    roughnessMin: clamp01(asFiniteNumber(settings.roughnessMin, DEFAULT_CONCRETE_SETTINGS.roughnessMin)),
    roughnessMax: clamp01(asFiniteNumber(settings.roughnessMax, DEFAULT_CONCRETE_SETTINGS.roughnessMax)),
    wearAmount: clamp01(asFiniteNumber(settings.wearAmount, DEFAULT_CONCRETE_SETTINGS.wearAmount)),
  };
}

function texturesForFace(face: ConcreteFace): ScannedConcreteTextures {
  const pack = scannedPacks[face];
  if (pack) return pack;
  return {
    color: placeholderColor,
    normal: placeholderNormal,
    roughness: placeholderRoughness,
    ao: placeholderAo,
    height: placeholderHeight,
  };
}

/** Aggregate secondary layer always reuses the interior (Concrete025) pack. */
function aggregateTextures(): ScannedConcreteTextures {
  return texturesForFace('interior');
}

function createUniforms(settings: ConcreteMaterialSettings, face: ConcreteFace): ConcreteUniforms {
  const maps = texturesForFace(face);
  const agg = aggregateTextures();
  return {
    uConcreteTextureScale: { value: settings.textureScale },
    uConcreteNormalStrength: { value: settings.normalStrength },
    uConcreteMacroVariation: { value: settings.macroVariation },
    uConcreteMoisture: { value: settings.moisture },
    uConcreteInteriorAmount: { value: settings.interiorAmount },
    uConcreteInteriorMode: { value: face === 'interior' ? 1 : 0 },
    uConcreteQuality: { value: qualityValue(settings.quality) },
    uConcreteSeed: { value: settings.seed },
    uConcreteSourceMode: { value: settings.source === 'scanned-pbr' ? 1 : 0 },
    uConcreteSurfaceState: { value: surfaceStateUniformValue(settings.surfaceState) },
    uConcreteMap: { value: maps.color },
    uConcreteNormalMap: { value: maps.normal },
    uConcreteRoughnessMap: { value: maps.roughness },
    uConcreteAoMap: { value: maps.ao },
    uConcreteHeightMap: { value: maps.height },
    uConcreteAggMap: { value: agg.color },
    uConcreteAggNormalMap: { value: agg.normal },
    uConcreteAggRoughnessMap: { value: agg.roughness },
    uConcreteAggHeightMap: { value: agg.height },
    uConcreteBaseTint: { value: new THREE.Color(settings.baseTint) },
    uConcreteRoughnessMin: { value: Math.min(settings.roughnessMin, settings.roughnessMax) },
    uConcreteRoughnessMax: { value: Math.max(settings.roughnessMin, settings.roughnessMax) },
    uConcreteWearAmount: { value: settings.wearAmount },
  };
}

function syncUniforms(state: ConcreteMaterialState): void {
  const { settings, uniforms, face } = state;
  uniforms.uConcreteTextureScale.value = settings.textureScale;
  uniforms.uConcreteNormalStrength.value = settings.normalStrength;
  uniforms.uConcreteMacroVariation.value = settings.macroVariation;
  uniforms.uConcreteMoisture.value = settings.moisture;
  uniforms.uConcreteInteriorAmount.value = settings.interiorAmount;
  uniforms.uConcreteQuality.value = qualityValue(settings.quality);
  uniforms.uConcreteSeed.value = settings.seed;
  uniforms.uConcreteSourceMode.value = settings.source === 'scanned-pbr' ? 1 : 0;
  uniforms.uConcreteSurfaceState.value = surfaceStateUniformValue(settings.surfaceState);
  uniforms.uConcreteInteriorMode.value = face === 'interior' ? 1 : 0;

  if (settings.source === 'scanned-pbr') {
    const maps = texturesForFace(face);
    const agg = aggregateTextures();
    uniforms.uConcreteMap.value = maps.color;
    uniforms.uConcreteNormalMap.value = maps.normal;
    uniforms.uConcreteRoughnessMap.value = maps.roughness;
    uniforms.uConcreteAoMap.value = maps.ao;
    uniforms.uConcreteHeightMap.value = maps.height;
    uniforms.uConcreteAggMap.value = agg.color;
    uniforms.uConcreteAggNormalMap.value = agg.normal;
    uniforms.uConcreteAggRoughnessMap.value = agg.roughness;
    uniforms.uConcreteAggHeightMap.value = agg.height;
  }

  uniforms.uConcreteBaseTint.value.set(settings.baseTint);
  uniforms.uConcreteRoughnessMin.value = Math.min(settings.roughnessMin, settings.roughnessMax);
  uniforms.uConcreteRoughnessMax.value = Math.max(settings.roughnessMin, settings.roughnessMax);
  uniforms.uConcreteWearAmount.value = settings.wearAmount;
}

function compileConcreteShader(
  shader: { uniforms: Record<string, { value: unknown }>; vertexShader: string; fragmentShader: string },
  state: ConcreteMaterialState,
): void {
  Object.assign(shader.uniforms, state.uniforms);
  assignDamageFieldUniforms(shader);
  assignDamageDecalUniforms(shader);

  // Vertex: rest position + rest normal only. No micro-normal FBM (per-fragment now).
  shader.vertexShader = shader.vertexShader
    .replace('#include <common>', `${VERTEX_INJECTION}\n#include <common>`)
    .replace(
      '#include <worldpos_vertex>',
      `#include <worldpos_vertex>
vConcreteRestPosition = surfaceRestPosition;
// Rest-space geometric normal only — micro detail is derivative-based in the fragment shader.
vConcreteRestNormal = normalize(normal);
${DAMAGE_FIELD_VERTEX_SAMPLE}
${DAMAGE_DECAL_VERTEX_SAMPLE}
`,
    );

  // Color: procedural concrete fields OR scanned triplanar albedo + spatial state masks.
  shader.fragmentShader = shader.fragmentShader
    .replace('#include <common>', `${FRAGMENT_INJECTION}\n#include <common>`)
    .replace(
      '#include <color_fragment>',
      `#include <color_fragment>
// Geometry normals and positions are both stored in the same wall-rest space.
vec3 concreteRestN = normalize(vConcreteRestNormal);
float concreteSt = uConcreteSurfaceState;
// Safe defaults for procedural globals (scanned path never runs surface fields).
gSurfaceSpecAaRough = 0.0;
gConcreteCement = 0.5;
gConcretePores = 0.0;
gConcreteAggregate = 0.0;
gConcreteHeight = 0.5;
gConcreteLodFade = 1.0;
gConcreteMicroField = surfaceMicroField(
  vConcreteRestPosition,
  max(uConcreteTextureScale * 0.12, 0.018),
  uConcreteSeed + 31.0
);
gConcreteLodFade = surfaceMicroFade(
  vConcreteRestPosition,
  max(uConcreteTextureScale * 0.12, 0.018)
);
// Masks once; specialized masks are branch-only inside concreteStateMasks.
concreteStateMasks(
  vConcreteRestPosition,
  concreteRestN,
  gConcreteMacroField,
  gConcreteAgedMask,
  gConcreteWetMask,
  gConcreteDustMask,
  gConcreteAggMask,
  gConcreteRunoffMask,
  gConcreteEfflorescenceMask,
  gConcreteCrackMask,
  gConcreteEdgeMoistureMask,
  gConcretePoolMask
);
gConcreteDirtMask = gConcreteAgedMask;
gConcreteDampMask = 0.0;
float concreteInterior = uConcreteInteriorMode * clamp(uConcreteInteriorAmount, 0.0, 1.0);
float concreteMacroVar = (gConcreteMacroField - 0.5) * 2.0 * uConcreteMacroVariation;

if (uConcreteSourceMode > 0.5) {
  // --- Scanned PBR path (ambientCG maps, rest-position triplanar) ---
  // textureScale = meters/tile; rest meters → tile UVs via 1 / metersPerTile.
  float concreteScale = max(uConcreteTextureScale, 0.05);
  vec3 concretePos = vConcreteRestPosition * (1.0 / concreteScale);
  vec3 concreteW = concreteTriplanarWeights(concreteRestN);
  vec3 concreteAlbedo = concreteSampleTriplanar(uConcreteMap, concretePos, concreteW).rgb;
  // Height is sampled in the same anti-tiled triplanar basis and reused by the
  // derivative relief normal below. Keep relief restrained to avoid silhouette displacement.
  gConcreteHeight = concreteSampleTriplanar(uConcreteHeightMap, concretePos, concreteW).r;

  // Scanned Concrete014 carries a cool cast-concrete read. Keep its PBR maps and
  // texture detail, but desaturate the intact exterior hero surface toward a
  // cool neutral cast-concrete value. Interior Concrete025 remains untouched.
  if (concreteSt < 0.5 && uConcreteInteriorMode < 0.5) {
    float concreteHeroLuma = dot(concreteAlbedo, vec3(0.2126, 0.7152, 0.0722));
    vec3 concreteHeroNeutral = concreteHeroLuma * vec3(0.98, 1.0, 1.02);
    // Keep the scan's measured value range; only remove the clearly warm cast.
    concreteAlbedo = mix(concreteAlbedo, concreteHeroNeutral, 0.34);
  }

  // Gentle macro variation — keep photographic grain readable under PMREM.
  concreteAlbedo *= 1.0 + concreteMacroVar * 0.12;
  // Shared fine cement grain keeps the scan from looking airbrushed at close range.
  concreteAlbedo *= 0.972 + gConcreteMicroField * 0.056;

  // Surface-state spatial overlays (masks drive albedo here; roughness/normal later).
  if (concreteSt > 0.5 && concreteSt < 1.5) {
    // Aged: warm gray/brown staining + darker vertical runoff, not uniform recolor.
    vec3 stain = vec3(0.72, 0.66, 0.58);
    vec3 runoffTint = vec3(0.52, 0.48, 0.44);
    concreteAlbedo = mix(concreteAlbedo, concreteAlbedo * stain, gConcreteDirtMask * 0.62);
    concreteAlbedo = mix(concreteAlbedo, concreteAlbedo * runoffTint, gConcreteRunoffMask * 0.5);
    concreteAlbedo = mix(concreteAlbedo, vec3(0.86, 0.88, 0.87), gConcreteEfflorescenceMask * 0.28);
    concreteAlbedo = mix(concreteAlbedo, concreteAlbedo * vec3(0.34, 0.32, 0.3), gConcreteCrackMask * 0.7);
  } else if (concreteSt > 1.5 && concreteSt < 2.5) {
    // Wet: darken only where wet mask is high; dry islands stay scan albedo.
    concreteAlbedo *= mix(1.0, 0.68, gConcreteWetMask);
  } else if (concreteSt > 2.5 && concreteSt < 3.5) {
    // Dusty: pale warmer flatter coating with broad coverage on vertical walls.
    vec3 dusty = mix(concreteAlbedo, vec3(dot(concreteAlbedo, vec3(0.333))), 0.35);
    dusty = mix(dusty, vec3(0.9, 0.86, 0.78), 0.55);
    concreteAlbedo = mix(concreteAlbedo, dusty, gConcreteDustMask * 0.88);
  } else if (concreteSt > 3.5) {
    // Aggregate: secondary Concrete025 in clear patches (exterior); interior already is 025.
    vec3 aggAlbedo = concreteSampleTriplanar(uConcreteAggMap, concretePos * 1.12, concreteW).rgb;
    float aggHeight = concreteSampleTriplanar(uConcreteAggHeightMap, concretePos * 1.12, concreteW).r;
    gConcreteHeight = mix(gConcreteHeight, aggHeight, gConcreteAggMask * 0.78);
    if (uConcreteInteriorMode > 0.5) {
      concreteAlbedo *= mix(1.0, 1.06, concreteInterior * 0.35);
      concreteAlbedo = mix(concreteAlbedo, mix(concreteAlbedo, aggAlbedo, 0.35), gConcreteAggMask * 0.55);
    } else {
      concreteAlbedo = mix(concreteAlbedo, aggAlbedo, gConcreteAggMask * 0.92);
    }
  } else {
    // Hero: restrained variation only (macro already applied).
    concreteAlbedo *= mix(1.0, 1.03, concreteInterior * 0.2);
  }

  if (uConcreteInteriorMode > 0.5) {
    vec3 concreteBreakS = vec3(0.710, 0.682, 0.635);
    float concFpS = surfacePixelFootprint(vConcreteRestPosition);
    float concFracS = surfaceNoiseBL(concretePos * 2.2 + vec3(9.0, 2.0, 14.0), uConcreteSeed * 19.19, concreteScale / 2.2, concFpS);
    float concPitS = surfaceNoiseBL(concretePos * 9.5 + vec3(6.0, 3.0, 17.0), uConcreteSeed * 19.19, concreteScale / 9.5, concFpS);
    float pitAmtS = smoothstep(0.58, 0.88, concPitS) * surfaceOctaveFade(concreteScale / 9.5, concFpS);
    concreteAlbedo = mix(concreteBreakS * 0.52, concreteBreakS * 1.24, concFracS);
    concreteAlbedo = mix(concreteAlbedo, concreteAlbedo * vec3(0.38, 0.36, 0.32), pitAmtS * 0.74);
    concreteAlbedo *= 0.97 + gConcreteMicroField * 0.06;
    gConcreteHeight = mix(0.38, 0.68, concFracS) - pitAmtS * 0.08;
  } else if (concreteSt < 0.5) {
    // Moisture is a wet-state mask today; on hero, extra moisture above the 0.05
    // default darkens patches so the lab knob is live without changing defaults.
    float heroMoist = max(0.0, uConcreteMoisture - 0.05);
    concreteAlbedo *= mix(1.0, 0.74, heroMoist * mix(0.28, 0.82, gConcreteMacroField));
  }

  diffuseColor.rgb = concreteAlbedo;
} else {
  // --- Procedural concrete path: expensive fields only here ---
  concreteSurfaceFields(
    vConcreteRestPosition,
    gConcreteCement,
    gConcretePores,
    gConcreteAggregate,
    gConcreteHeight
  );
  // Procedural albedo is now absolute. The old path multiplied this by the
  // darker profile tint a second time, making the hero look under-lit.
  // Exterior cement sits mid-grey (formed face); interior stays the paler mix.
  vec3 cementTint = mix(vec3(0.214, 0.224, 0.236), vec3(0.26, 0.28, 0.30), concreteInterior);
  vec3 poreTint = vec3(0.12, 0.14, 0.16);
  float metersPerTileP = max(uConcreteTextureScale, 0.05);
  vec3 tileP = vConcreteRestPosition * (1.0 / metersPerTileP);
  vec3 mineralTint = mix(
    vec3(0.62, 0.65, 0.68),
    vec3(0.82, 0.84, 0.86),
    surfaceNoiseBL(tileP * 14.0 + vec3(3.0, 1.0, 7.0), uConcreteSeed * 19.19, metersPerTileP / 14.0, surfacePixelFootprint(vConcreteRestPosition))
  );
  // Formed face: low-contrast cement grain. Interior still mixes mineral flecks.
  vec3 procAlbedo = mix(cementTint, cementTint * (0.96 + gConcreteCement * 0.08), mix(0.28, 0.7, concreteInterior));
  procAlbedo = mix(procAlbedo, poreTint, gConcretePores * mix(0.62, 0.46, concreteInterior));
  procAlbedo = mix(procAlbedo, mineralTint, gConcreteAggregate * mix(0.0, 0.9, concreteInterior));

  vec3 faceTint = mix(vec3(0.99, 0.995, 1.0), vec3(1.0, 1.01, 1.02), concreteInterior);
  procAlbedo *= faceTint;
  procAlbedo *= 1.0 + concreteMacroVar * mix(0.07, 0.35, concreteInterior);
  procAlbedo *= mix(0.992, 0.972, concreteInterior) + gConcreteMicroField * mix(0.014, 0.056, concreteInterior);

  // Formed-concrete weathering: broad low-contrast stains, sparse runoff, faint
  // form-panel seams. Intentionally NOT the dense blotch/fleck field that read
  // as aggregate break-face at wide range.
  if (uConcreteInteriorMode < 0.5) {
    float concFpW = surfacePixelFootprint(vConcreteRestPosition);
    float stainWl = 1.85;
    float stainFade = surfaceOctaveFade(stainWl, concFpW);
    float stain = surfaceNoiseBL(
      vConcreteRestPosition / stainWl + vec3(2.0, 8.0, 5.0),
      uConcreteSeed * 19.19,
      stainWl,
      concFpW
    );
    float driftWl = 3.15;
    float driftFade = surfaceOctaveFade(driftWl, concFpW);
    float drift = surfaceNoiseBL(
      vConcreteRestPosition / driftWl + vec3(1.0, 6.0, 3.0),
      uConcreteSeed * 19.19,
      driftWl,
      concFpW
    );
    float stainPatch = smoothstep(0.38, 0.72, stain);
    float weatherAmt = clamp(uConcreteMacroVariation, 0.0, 1.0);
    procAlbedo *= mix(1.0, mix(0.97, 1.014, stain), (0.06 + 0.12 * weatherAmt) * stainFade);
    procAlbedo *= mix(1.0, mix(0.985, 1.010, drift), 0.14 * driftFade);
    procAlbedo = mix(procAlbedo, procAlbedo * vec3(0.986, 0.988, 0.990), stainPatch * 0.10 * stainFade);

    // Sparse vertical water streaks from the top edge / random ledges.
    float streak = surfaceNoiseBL(
      vec3(vConcreteRestPosition.x * 1.05, vConcreteRestPosition.y * 0.09, vConcreteRestPosition.z * 1.05) + vec3(5.0, 0.0, 11.0),
      uConcreteSeed * 19.19,
      0.95,
      concFpW
    );
    float streakPeak = smoothstep(0.88, 0.97, streak);
    float streakMod = surfaceNoiseBL(
      vec3(vConcreteRestPosition.x * 0.32, vConcreteRestPosition.y * 0.07, vConcreteRestPosition.z * 0.32) + vec3(19.0, 4.0, 8.0),
      uConcreteSeed * 19.19,
      3.1,
      concFpW
    );
    float fromTop = mix(0.18, 1.0, clamp((4.2 - vConcreteRestPosition.y) / 3.4, 0.0, 1.0));
    float streaks = streakPeak * mix(0.25, 1.0, smoothstep(0.42, 0.78, streakMod)) * fromTop * surfaceOctaveFade(0.08, concFpW);
    procAlbedo *= mix(1.0, 0.92, streaks * 0.55);

    // Faint horizontal form-panel seams — 2–3 across the 4.2 m wall height.
    float seamPeriod = 1.40;
    float seamY = abs(fract(vConcreteRestPosition.y / seamPeriod + 0.12) - 0.5);
    float seam = (1.0 - smoothstep(0.0, 0.0075, seamY)) * surfaceOctaveFade(0.028, concFpW);
    procAlbedo *= mix(1.0, 0.99, seam);

    gConcreteDampMask = clamp(stainPatch * 0.38 * stainFade + streaks * 0.78, 0.0, 1.0);
    gConcreteHeight += (stain - 0.5) * 0.010 * stainFade + (drift - 0.5) * 0.006 * driftFade;

    // Close-range cement grain is LIVE and camera-footprint faded. Baking it
    // into the maps (aniso 8) reintroduces wide-range speckle, so skip it
    // whenever uBakeOutput is the albedo/roughness/normal capture pass.
    if (uBakeOutput < 0.5) {
      float fineWl = 0.014;
      float fineFade = surfaceOctaveFade(fineWl, concFpW);
      float fine = surfaceNoiseBL(
        vConcreteRestPosition / fineWl + vec3(13.0, 2.0, 9.0),
        uConcreteSeed * 19.19,
        fineWl,
        concFpW
      );
      float fine2Wl = 0.007;
      float fine2Fade = surfaceOctaveFade(fine2Wl, concFpW);
      float fine2 = surfaceNoiseBL(
        vConcreteRestPosition / fine2Wl + vec3(4.0, 17.0, 6.0),
        uConcreteSeed * 19.19,
        fine2Wl,
        concFpW
      );
      procAlbedo *= 1.0 + (fine - 0.5) * 0.23 * fineFade + (fine2 - 0.5) * 0.14 * fine2Fade;
      float fadePit = surfaceOctaveFade(0.012, concFpW);
      float pitCell = concreteCellular(vConcreteRestPosition * 3.35 + vec3(4.0, 11.0, 2.0));
      float pitGate = surfaceNoiseBL(
        vConcreteRestPosition * 0.58 + vec3(9.0, 3.0, 17.0),
        uConcreteSeed * 19.19,
        1.72,
        concFpW
      );
      float pits = (1.0 - smoothstep(0.008, 0.028, pitCell)) * smoothstep(0.66, 0.90, pitGate) * fadePit;
      procAlbedo = mix(procAlbedo, procAlbedo * vec3(0.62, 0.64, 0.66), pits * 0.72);
      gConcretePores = pits;
    }
  }

  if (concreteSt > 0.5 && concreteSt < 1.5) {
    // Aged: warm brown staining + darker runoff — unmistakable vs hero at full wall.
    procAlbedo = mix(procAlbedo, procAlbedo * vec3(0.7, 0.64, 0.56), gConcreteDirtMask * 0.62);
    procAlbedo = mix(procAlbedo, procAlbedo * vec3(0.5, 0.47, 0.43), gConcreteRunoffMask * 0.52);
    procAlbedo = mix(procAlbedo, vec3(0.86, 0.88, 0.87), gConcreteEfflorescenceMask * 0.24);
    procAlbedo = mix(procAlbedo, procAlbedo * vec3(0.3, 0.28, 0.26), gConcreteCrackMask * 0.6);
  } else if (concreteSt > 1.5 && concreteSt < 2.5) {
    procAlbedo *= mix(1.0, 0.66, gConcreteWetMask);
  } else if (concreteSt > 2.5 && concreteSt < 3.5) {
    // Dusty: pale warmer coating, broad coverage.
    vec3 dusty = mix(procAlbedo, vec3(0.88, 0.84, 0.76), 0.82);
    procAlbedo = mix(procAlbedo, dusty, gConcreteDustMask * 0.9);
  } else if (concreteSt > 3.5) {
    // Aggregate: more mineral flecks in patches on top of base inclusions.
    procAlbedo = mix(procAlbedo, mineralTint * 1.08, gConcreteAggMask * 0.85);
    procAlbedo = mix(procAlbedo, mineralTint * 0.92, gConcreteAggregate * 0.35);
  }

  if (uConcreteInteriorMode > 0.5) {
    // Fresh unweathered fracture. Tonal anchor: appearance.fragment.interior #b5aea2.
    // Coarse fracture grain + aggregate/pit shadows — albedo contrast.
    vec3 concreteBreak = vec3(0.710, 0.682, 0.635);
    float concFpI = surfacePixelFootprint(vConcreteRestPosition);
    float concFrac = surfaceNoiseBL(tileP * 2.2 + vec3(9.0, 2.0, 14.0), uConcreteSeed * 19.19, metersPerTileP / 2.2, concFpI);
    float concFrac2 = surfaceNoiseBL(tileP * 5.4 + vec3(1.0, 11.0, 4.0), uConcreteSeed * 19.19, metersPerTileP / 5.4, concFpI);
    float concPit = surfaceNoiseBL(tileP * 9.5 + vec3(6.0, 3.0, 17.0), uConcreteSeed * 19.19, metersPerTileP / 9.5, concFpI);
    float pitAmt = smoothstep(0.58, 0.88, concPit) * surfaceOctaveFade(metersPerTileP / 9.5, concFpI);
    procAlbedo = mix(concreteBreak * 0.52, concreteBreak * 1.24, mix(concFrac, concFrac2, 0.38));
    procAlbedo = mix(procAlbedo, procAlbedo * vec3(0.38, 0.36, 0.32), pitAmt * 0.74);
    procAlbedo = mix(procAlbedo, mineralTint * 1.04, gConcreteAggregate * 0.55);
    procAlbedo *= 0.97 + gConcreteMicroField * 0.06;
    gConcretePores = 0.0;
    gConcreteHeight = mix(0.38, 0.68, mix(concFrac, concFrac2, 0.35)) - pitAmt * 0.08;
  } else if (concreteSt < 0.5) {
    float heroMoistP = max(0.0, uConcreteMoisture - 0.05);
    procAlbedo *= mix(1.0, 0.74, heroMoistP * mix(0.28, 0.82, gConcreteMacroField));
  }

  diffuseColor.rgb *= procAlbedo;
}
#ifdef USE_DAMAGE_FIELD
fillDamageGlobals(vConcreteRestPosition, concreteRestN);
#ifdef USE_DAMAGE_DECAL_ATLAS
sampleDamageDecals(vConcreteRestPosition, concreteRestN);
// Atlas depth + tiny crack height dip (too much reads as white specular rims).
gConcreteHeight = clamp(gConcreteHeight - gDecalDepth * 0.16 - gDecalCrack * 0.022 + gDecalRim * 0.05, 0.0, 1.0);
#endif
float concreteFresh = 1.0 - gDamageAge * 0.32;
#ifdef USE_DAMAGE_DECAL_ATLAS
// D7: field bruise is a whisper through chew-modulated gDamageBlotch only.
// Never raw gDamageVis / damageFieldVisual — those are the 3D-field texel boxes.
// Atlas G (512px, ragged) carries the visible albedo darkening.
vec3 concreteBlotchTint = diffuseColor.rgb * vec3(0.86, 0.84, 0.80);
concreteBlotchTint = mix(concreteBlotchTint, mix(concreteBlotchTint, diffuseColor.rgb * 0.92, 0.4), 1.0 - concreteFresh);
diffuseColor.rgb = mix(diffuseColor.rgb, min(diffuseColor.rgb, concreteBlotchTint), gDamageBlotch * 0.03 * concreteFresh);
// D8: crater grain — slightly coarser than intact micro (band-limited).
float craterFp = surfacePixelFootprint(vConcreteRestPosition);
float craterMeters = max(uConcreteTextureScale, 0.05);
vec3 craterP = vConcreteRestPosition * (1.0 / craterMeters);
float gConcreteCraterGrain = surfaceNoiseBL(craterP * 8.0 + vec3(5.0, 2.0, 9.0), uConcreteSeed * 19.19, craterMeters / 8.0, craterFp) * 0.52
  + surfaceNoiseBL(craterP * 16.0 + vec3(11.0, 4.0, 3.0), uConcreteSeed * 19.19, craterMeters / 16.0, craterFp) * 0.28
  + gConcreteAggregate * 0.12
  + gConcreteMicroField * 0.08;
// Mean crater tone from B-channel depth, then grain modulates both ways so
// the interior is exposed aggregate — not a min()-crushed soot wash.
vec3 concreteSpallMean = mix(
  diffuseColor.rgb * vec3(0.70, 0.67, 0.62),
  diffuseColor.rgb * vec3(0.48, 0.45, 0.40),
  clamp(gDecalDepth, 0.0, 1.0)
);
concreteSpallMean = mix(concreteSpallMean, mix(concreteSpallMean, diffuseColor.rgb * 0.82, 0.38), 1.0 - gDecalFresh);
float concreteSpallAmt = gDecalSpall * mix(0.82, 1.0, clamp(gDecalDepth, 0.0, 1.0));
vec3 concreteSpallTint = concreteSpallMean * mix(0.86, 1.14, gConcreteCraterGrain);
diffuseColor.rgb = mix(diffuseColor.rgb, concreteSpallTint, concreteSpallAmt);
// Lit rim just outside the G isoline — chipped edge catches light.
vec3 concreteRimTint = diffuseColor.rgb * mix(vec3(1.22, 1.18, 1.12), vec3(1.10, 1.08, 1.04), 1.0 - gDecalFresh);
diffuseColor.rgb = mix(diffuseColor.rgb, concreteRimTint, gDecalRim * 0.95);
// Crack: dark-grey concrete relative to local albedo (not absolute greys that lighten).
// Width-graded: edge lifts less, centerline darkest — never near-black, never lighter than base.
vec3 concreteCrackTint = diffuseColor.rgb * mix(vec3(0.62, 0.60, 0.58), vec3(0.42, 0.40, 0.38), gDecalCrackCore);
diffuseColor.rgb = mix(diffuseColor.rgb, min(diffuseColor.rgb, concreteCrackTint), gDecalCrack * 0.92);
#else
// Interior cut faces: field bruise + compact core (atlas is exterior-only).
vec3 concreteBruiseDark = diffuseColor.rgb * vec3(0.45, 0.43, 0.40);
vec3 concreteExposedCore = diffuseColor.rgb * mix(
  vec3(0.58, 0.55, 0.50),
  mix(vec3(0.50, 0.48, 0.44), vec3(0.62, 0.58, 0.52), gConcreteAggregate),
  clamp(gDamage, 0.0, 1.0)
);
vec3 concreteBlotchTint = mix(concreteBruiseDark, concreteExposedCore, mix(0.35, 0.7, clamp(gDamage, 0.0, 1.0)));
concreteBlotchTint = mix(concreteBlotchTint, mix(concreteBlotchTint, diffuseColor.rgb * 0.78, 0.35), 1.0 - concreteFresh);
diffuseColor.rgb = mix(diffuseColor.rgb, min(diffuseColor.rgb, concreteBlotchTint), gDamageBlotch * mix(0.4, 0.78, clamp(gDamage, 0.0, 1.0)) * concreteFresh);
diffuseColor.rgb = mix(diffuseColor.rgb, diffuseColor.rgb * vec3(0.28, 0.26, 0.24), gDamageCore * 0.72 * concreteFresh);
diffuseColor.rgb = mix(diffuseColor.rgb, diffuseColor.rgb * vec3(0.14, 0.13, 0.12), gDamageCrack * concreteFresh);
#endif
#endif
diffuseColor.rgb *= uConcreteBaseTint;
diffuseColor.rgb = mix(diffuseColor.rgb, diffuseColor.rgb * vec3(0.78, 0.74, 0.68), clamp(uConcreteWearAmount, 0.0, 1.0));
`,
    )
    .replace(
      '#include <roughnessmap_fragment>',
      `#include <roughnessmap_fragment>
float concreteStR = uConcreteSurfaceState;

if (uConcreteSourceMode > 0.5) {
  float concreteScaleR = max(uConcreteTextureScale, 0.05);
  vec3 concretePosR = vConcreteRestPosition * (1.0 / concreteScaleR);
  vec3 concreteWR = concreteTriplanarWeights(normalize(vConcreteRestNormal));
  float mapRoughRaw = concreteSampleTriplanar(uConcreteRoughnessMap, concretePosR, concreteWR).r;
  // The scan is close to white across most pixels. Remap it into a broad,
  // still-concrete highlight range before applying the lab control.
  float mapRough = mix(0.52, 0.82, mapRoughRaw);
  roughnessFactor = clamp(mapRough * mix(0.9, 1.08, roughnessFactor), 0.08, 0.94);
  // Macro variation on roughness (localized, not a global wetness).
  roughnessFactor = clamp(roughnessFactor + (gConcreteMacroField - 0.5) * uConcreteMacroVariation * 0.12, 0.08, 1.0);

  if (concreteStR > 0.5 && concreteStR < 1.5) {
    // Aged: localized roughness from stains/runoff, not uniform.
    roughnessFactor = clamp(
      roughnessFactor + gConcreteAgedMask * 0.12 + gConcreteEfflorescenceMask * 0.16
        + gConcreteCrackMask * 0.08 - gConcreteEdgeMoistureMask * 0.1,
      0.08,
      1.0
    );
  } else if (concreteStR > 1.5 && concreteStR < 2.5) {
    // Wet: lower roughness only in wet patches.
    float wetFilm = gConcreteWetMask * (0.68 + 0.32 * gConcretePoolMask);
    roughnessFactor = clamp(roughnessFactor - wetFilm * 0.56, 0.035, 1.0);
  } else if (concreteStR > 2.5 && concreteStR < 3.5) {
    roughnessFactor = clamp(roughnessFactor + gConcreteDustMask * 0.22, 0.12, 1.0);
  } else if (concreteStR > 3.5) {
    float aggRough = concreteSampleTriplanar(uConcreteAggRoughnessMap, concretePosR * 1.12, concreteWR).r;
    roughnessFactor = mix(roughnessFactor, aggRough * mix(0.9, 1.1, roughnessFactor), gConcreteAggMask * 0.82);
  }
} else {
  float concreteInteriorR = uConcreteInteriorMode * clamp(uConcreteInteriorAmount, 0.0, 1.0);
  roughnessFactor = clamp(
    roughnessFactor
      + (gConcreteCement - 0.5) * mix(0.045, 0.08, concreteInteriorR)
      + gConcretePores * 0.06
      + (gConcreteAggregate - 0.25) * 0.1 * mix(0.55, 1.0, concreteInteriorR)
      + (0.5 - gConcreteMacroField) * 0.05 * uConcreteMacroVariation,
    0.04,
    1.0
  );

  if (concreteStR > 0.5 && concreteStR < 1.5) {
    roughnessFactor = clamp(
      roughnessFactor + gConcreteAgedMask * 0.1 + gConcreteEfflorescenceMask * 0.14
        + gConcreteCrackMask * 0.07 - gConcreteEdgeMoistureMask * 0.08,
      0.04,
      1.0
    );
  } else if (concreteStR > 1.5 && concreteStR < 2.5) {
    roughnessFactor = clamp(roughnessFactor - gConcreteWetMask * (0.48 + gConcretePoolMask * 0.18), 0.035, 1.0);
  } else if (concreteStR > 2.5 && concreteStR < 3.5) {
    roughnessFactor = clamp(roughnessFactor + gConcreteDustMask * 0.2, 0.1, 1.0);
  } else if (concreteStR > 3.5) {
    roughnessFactor = clamp(roughnessFactor + gConcreteAggMask * 0.1 + gConcreteAggregate * 0.06, 0.06, 1.0);
  }
  roughnessFactor = clamp(roughnessFactor + (gConcreteMicroField - 0.5) * 0.08, 0.04, 1.0);
}
roughnessFactor = clamp(roughnessFactor + (1.0 - gConcreteLodFade) * 0.05, 0.04, 1.0);
// Sink Toksvig slot (filled after surfaceHeightRestNormal in normal_fragment_maps; reapplied there).
roughnessFactor = clamp(roughnessFactor + gSurfaceSpecAaRough, 0.04, 1.0);
#ifdef USE_DAMAGE_FIELD
#ifdef USE_DAMAGE_DECAL_ATLAS
roughnessFactor = clamp(
  roughnessFactor
    + gDamageBlotch * 0.03
    + gDecalSpall * (0.09 + gConcreteCraterGrain * 0.07)
    + gDecalDepth * 0.10
    + gDecalCrack * 0.08,
  0.04,
  1.0
);
#else
roughnessFactor = clamp(roughnessFactor + gDamageBlotch * 0.07 + gDamageCrack * 0.08, 0.04, 1.0);
#endif
#endif
if (uConcreteInteriorMode > 0.5) {
  roughnessFactor = clamp(roughnessFactor + 0.07, 0.12, 1.0);
} else if (uConcreteSurfaceState < 0.5) {
  roughnessFactor = clamp(
    roughnessFactor
      - max(0.0, uConcreteMoisture - 0.05) * 0.32
      - gConcreteDampMask * 0.14,
    0.04,
    1.0
  );
}
roughnessFactor = clamp(roughnessFactor, uConcreteRoughnessMin, max(uConcreteRoughnessMin, uConcreteRoughnessMax));
`,
    )
    .replace(
      '#include <normal_fragment_maps>',
      `#include <normal_fragment_maps>
float concreteStN = uConcreteSurfaceState;

// Local normal-strength scale from surface state (water film / dust flatten micro detail).
float concreteNScale = 1.0;
if (concreteStN > 1.5 && concreteStN < 2.5) {
  concreteNScale = mix(1.0, 0.22, gConcreteWetMask);
} else if (concreteStN > 2.5 && concreteStN < 3.5) {
  concreteNScale = mix(1.0, 0.28, gConcreteDustMask);
}

if (uConcreteSourceMode > 0.5) {
  float concreteScaleN = max(uConcreteTextureScale, 0.05);
  vec3 concretePosN = vConcreteRestPosition * (1.0 / concreteScaleN);
  vec3 concreteWN = concreteTriplanarWeights(normalize(vConcreteRestNormal));
  vec3 restDetail = concreteSampleTriplanarNormal(
    uConcreteNormalMap,
    concretePosN,
    concreteWN,
    normalize(vConcreteRestNormal)
  );

  // Combine scan normal with measured height relief; moisture/dust only damp
  // detail where their localized masks are active.
  // Keep scanned relief on the measured normal map until a continuous,
  // derivative-safe height field is available. The previous randomized height
  // sample created false ridges at every anti-tiling cell boundary.
  restDetail = normalize(restDetail);

  if (concreteStN > 3.5 && uConcreteInteriorMode < 0.5) {
    // Exterior aggregate: blend secondary Concrete025 normals in clear patches.
    vec3 aggDetail = concreteSampleTriplanarNormal(
      uConcreteAggNormalMap,
      concretePosN * 1.12,
      concreteWN,
      normalize(vConcreteRestNormal)
    );
    restDetail = normalize(mix(restDetail, aggDetail, gConcreteAggMask * 0.88));
  }

  // Transform only the perturbation into view space. Flat map → zero delta.
  // Slightly stronger map response so formwork/grain survives softer lighting.
  vec3 restDelta = restDetail - normalize(vConcreteRestNormal);
  normal = normalize(normal + (normalMatrix * restDelta) * uConcreteNormalStrength * 1.65 * concreteNScale);
} else {
  // Procedural: derivative-based per-fragment bump from concrete height field.
  float nStrength = uConcreteNormalStrength * concreteNScale;
  float concreteHeightFade = surfaceOctaveFade(
    max(uConcreteTextureScale, 0.05) / 48.0,
    surfacePixelFootprint(vConcreteRestPosition)
  );
  vec3 restBump = concreteHeightRestNormal(
    normalize(vConcreteRestNormal),
    vConcreteRestPosition,
    gConcreteHeight,
    nStrength * mix(0.28, 0.52, uConcreteInteriorMode) * concreteHeightFade
  );
  vec3 restDeltaP = restBump - normalize(vConcreteRestNormal);
  // Flat height / zero strength → restDeltaP ~ 0 (no normalize(0) path in height helper).
  normal = normalize(normal + normalMatrix * restDeltaP);
}
// Shared fine relief is layered after the source-specific normal branch so
// scanned and procedural concrete receive the same close-range material feel.
vec3 concreteMicroBump = surfaceMicroNormal(
  normalize(vConcreteRestNormal),
  vConcreteRestPosition,
  max(uConcreteTextureScale * 0.12, 0.018),
  uConcreteNormalStrength * concreteNScale * mix(0.12, 0.18, uConcreteInteriorMode),
  uConcreteSeed + 31.0
);
normal = normalize(normal + normalMatrix * (concreteMicroBump - normalize(vConcreteRestNormal)));
// Apply sink Toksvig after height normals fill gSurfaceSpecAaRough (three.js runs this after roughnessmap_fragment).
roughnessFactor = clamp(roughnessFactor + gSurfaceSpecAaRough, 0.04, 1.0);
float concreteRSpan = max(0.96, 1e-4);
float concreteRT = clamp((roughnessFactor - 0.04) / concreteRSpan, 0.0, 1.0);
roughnessFactor = mix(uConcreteRoughnessMin, max(uConcreteRoughnessMin, uConcreteRoughnessMax), concreteRT);
#ifdef USE_DAMAGE_FIELD
// Cracks are albedo-only hairlines. Normal emboss read as thick worms.
#endif
`
    )
    .replace(
      '#include <aomap_fragment>',
      `#include <aomap_fragment>
if (uConcreteSourceMode > 0.5) {
  float concreteScaleAo = max(uConcreteTextureScale, 0.05);
  vec3 concretePosAo = vConcreteRestPosition * (1.0 / concreteScaleAo);
  vec3 concreteWAo = concreteTriplanarWeights(normalize(vConcreteRestNormal));
  float concreteAo = concreteSampleTriplanar(uConcreteAoMap, concretePosAo, concreteWAo).r;
  // AO attenuates ambient/indirect light rather than baking shadows into albedo.
  reflectedLight.indirectDiffuse *= mix(1.0, concreteAo, 0.7);
  // Aged cavities darken ambient response while wet film lifts grazing/specular response.
  reflectedLight.indirectDiffuse *= 1.0 - gConcreteCrackMask * 0.28;
  float concreteWetFilm = gConcreteWetMask * (0.72 + 0.28 * gConcretePoolMask);
  float concreteWetGrazing = 1.0 + (1.0 - saturate(dot(geometryNormal, geometryViewDir))) * 0.5;
  // Clearcoat-like film response without changing the public MeshStandard API.
  reflectedLight.directSpecular *= 1.0 + concreteWetFilm * 0.72 * concreteWetGrazing;
  reflectedLight.indirectSpecular *= 1.0 + concreteWetFilm * 0.38 * concreteWetGrazing;
} else {
  reflectedLight.indirectDiffuse *= 1.0 - gConcreteCrackMask * 0.2;
}
#ifdef USE_DAMAGE_FIELD
#ifdef USE_DAMAGE_DECAL_ATLAS
// AO recess band outside crack + slight in-crack / crater-depth ambient sink.
reflectedLight.indirectDiffuse *= 1.0 - gDecalAo * 0.48 - gDecalCrack * 0.18 - gDecalDepth * 0.12;
#endif
#endif`,
    );
}

function applyBaseMaterialDefaults(
  material: THREE.MeshStandardMaterial,
  state: ConcreteMaterialState,
): void {
  const { settings, face } = state;
  applySurfaceMaterialDefaults(material, {
    roughness: settings.roughness,
    shaderOwnsAlbedo: true,
    // Concrete is dielectric. A tiny procedural sheen is useful for the
    // authored branch, while scanned PBR keeps its metalness physically zero.
    metalness: settings.source === 'scanned-pbr' ? 0 : (face === 'interior' ? 0 : 0.04),
  });
}

export function createConcreteMaterial(
  params: MaterialParams,
  settings: ConcreteMaterialSettings,
  face: ConcreteFace,
): THREE.MeshStandardMaterial {
  const normalizedSettings = normalizeSettings(settings);
  const material = createSurfaceMaterial(params, {
    roughness: normalizedSettings.roughness,
    shaderOwnsAlbedo: true,
    metalness: normalizedSettings.source === 'scanned-pbr' ? 0 : (face === 'interior' ? 0 : 0.04),
  });

  const state: ConcreteMaterialState = {
    face,
    settings: normalizedSettings,
    uniforms: createUniforms(normalizedSettings, face),
  };
  applyBaseMaterialDefaults(material, state);

  material.onBeforeCompile = (shader) => {
    compileConcreteShader(shader, state);
    applyBakeShaderHooks(shader, material);
  };
  // Atlas decals are exterior-only: cut faces use the interior material variant
  // and must not paint authored crack networks across fracture surfaces.
  const decalEnabled = face === 'exterior';
  if (decalEnabled) applyDamageDecalAtlasDefine(material);
  // Custom uniforms/program path; force rebuild when source mode flips later.
  material.customProgramCacheKey = () =>
    surfaceProgramCacheKey(
      'concrete',
      12,
      state.settings.source,
      state.face,
      state.settings.quality,
      decalEnabled ? DAMAGE_DECAL_ATLAS_DEFINE : 'no-decal',
      ...damageFieldProgramParts(),
    );
  concreteStates.set(material, state);
  registerDamageFieldConsumer(material);
  return material;
}

export function isConcreteMaterial(material: THREE.Material): material is THREE.MeshStandardMaterial {
  return material instanceof THREE.MeshStandardMaterial && concreteStates.has(material);
}

export function getConcreteSource(material: THREE.Material): ConcreteSourceMode | null {
  const state = concreteStates.get(material);
  return state?.settings.source ?? null;
}

export function getConcreteSurfaceState(material: THREE.Material): ConcreteSurfaceState | null {
  const state = concreteStates.get(material);
  return state?.settings.surfaceState ?? null;
}

/**
 * Recreate a concrete material rather than using Material.clone(), which does
 * not retain an onBeforeCompile closure or its registered concrete state.
 */
export function cloneConcreteMaterial(source: THREE.Material): THREE.MeshStandardMaterial {
  const state = concreteStates.get(source);
  if (!state || !(source instanceof THREE.MeshStandardMaterial)) {
    throw new TypeError('cloneConcreteMaterial expects a material created by createConcreteMaterial');
  }

  const clone = createConcreteMaterial(
    {
      color: source.color.clone(),
      roughness: source.roughness,
      metalness: source.metalness,
      flatShading: source.flatShading,
      side: source.side,
      transparent: source.transparent,
      opacity: source.opacity,
      depthWrite: source.depthWrite,
      alphaTest: source.alphaTest,
      vertexColors: source.vertexColors,
      name: source.name,
    },
    state.settings,
    state.face,
  );
  copySurfaceMaterialProperties(source, clone);
  return clone;
}

export function updateConcreteMaterial(
  material: THREE.Material,
  settings: ConcreteMaterialSettings,
): void {
  const state = concreteStates.get(material);
  if (!state) {
    throw new TypeError('updateConcreteMaterial expects a material created by createConcreteMaterial');
  }

  const previousSource = state.settings.source;
  state.settings = normalizeSettings(settings);
  syncUniforms(state);

  if (material instanceof THREE.MeshStandardMaterial) {
    applyBaseMaterialDefaults(material, state);
    if (previousSource !== state.settings.source) {
      material.needsUpdate = true;
    }
  }
}

/** Rebind shared scan textures onto an existing material after async load. */
export function bindScannedConcreteTextures(material: THREE.Material): void {
  const state = concreteStates.get(material);
  if (!state) return;
  syncUniforms(state);
  if (material instanceof THREE.MeshStandardMaterial) {
    material.needsUpdate = true;
  }
}

const CONCRETE_CONTROLS: SurfaceControlSpec[] = [
  ...commonSurfaceControls(DEFAULT_CONCRETE_SETTINGS),
  {
    key: 'macroVariation',
    label: 'Weathering mottling',
    kind: 'range',
    min: 0,
    max: 1,
    step: 0.01,
    default: DEFAULT_CONCRETE_SETTINGS.macroVariation,
    group: 'detail',
  },
  {
    key: 'moisture',
    label: 'Moisture (darker + shinier)',
    kind: 'range',
    min: 0,
    max: 1,
    step: 0.01,
    default: DEFAULT_CONCRETE_SETTINGS.moisture,
    group: 'wear',
  },
];

export const SURFACE_DEFINITION: SurfaceDefinition<ConcreteMaterialSettings> = {
  id: 'concrete',
  label: 'Concrete',
  appearance: appearanceDescriptor,
  physics: DESTRUCTION_PROFILES.concrete,
  defaultSettings: DEFAULT_CONCRETE_SETTINGS,
  controls: CONCRETE_CONTROLS,
  settingsFromControls(values) {
    return {
      ...DEFAULT_CONCRETE_SETTINGS,
      seed: asFiniteNumber(values.seed, DEFAULT_CONCRETE_SETTINGS.seed),
      textureScale: asFiniteNumber(values.textureScale, DEFAULT_CONCRETE_SETTINGS.textureScale),
      baseTint: asHexColor(values.baseTint, DEFAULT_CONCRETE_SETTINGS.baseTint),
      roughnessMin: asFiniteNumber(values.roughnessMin, DEFAULT_CONCRETE_SETTINGS.roughnessMin),
      roughnessMax: asFiniteNumber(values.roughnessMax, DEFAULT_CONCRETE_SETTINGS.roughnessMax),
      normalStrength: asFiniteNumber(values.normalStrength, DEFAULT_CONCRETE_SETTINGS.normalStrength),
      wearAmount: asFiniteNumber(values.wearAmount, DEFAULT_CONCRETE_SETTINGS.wearAmount),
      macroVariation: asFiniteNumber(values.macroVariation, DEFAULT_CONCRETE_SETTINGS.macroVariation),
      moisture: asFiniteNumber(values.moisture, DEFAULT_CONCRETE_SETTINGS.moisture),
    };
  },
  createMaterial(params, settings, face: SurfaceFace) {
    return createConcreteMaterial(params, settings, face);
  },
  updateMaterial: updateConcreteMaterial,
  cloneMaterial: cloneConcreteMaterial,
  isMaterial: isConcreteMaterial,
};
